// WYSIWYG editor for blog bodies (TipTap). Outputs HTML via onChange;
// the backend sanitizes it (nh3) on save, so the editor can be permissive.
//
// Remount with a `key` when switching posts so the initial content resets.

import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useRef, type ReactNode } from "react";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  /** Uploads a file and returns its URL; used by the image button. */
  onImageUpload: (file: File) => Promise<string>;
}

function ToolbarButton({
  active,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="px-2 py-1 rounded font-mono text-[12px] cursor-pointer"
      style={{
        background: active ? "rgb(var(--color-accent-bg))" : "transparent",
        border: `1px solid ${active ? "rgb(var(--color-accent))" : "rgb(var(--color-stroke))"}`,
        color: active ? "rgb(var(--color-accent))" : "rgb(var(--color-muted))",
      }}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange, onImageUpload }: RichTextEditorProps) {
  const fileInput = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, autolink: true }),
      Image,
    ],
    content: value,
    editorProps: {
      attributes: { class: "blog-body focus:outline-none min-h-[240px]" },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  if (!editor) return null;

  function setLink(ed: Editor) {
    const prev = ed.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL", prev ?? "https://");
    if (url === null) return;
    if (url === "") {
      ed.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    ed.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }

  async function pickImage(file: File) {
    try {
      const url = await onImageUpload(file);
      editor!.chain().focus().setImage({ src: url }).run();
    } catch {
      // surfaced by the caller's toast
    }
  }

  return (
    <div
      className="rounded"
      style={{ border: "1px solid rgb(var(--color-stroke))", background: "rgb(var(--color-bg))" }}
    >
      <div
        className="flex flex-wrap gap-1 p-2 border-b"
        style={{ borderColor: "rgb(var(--color-stroke))" }}
      >
        <ToolbarButton title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <b>B</b>
        </ToolbarButton>
        <ToolbarButton title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <i>I</i>
        </ToolbarButton>
        <ToolbarButton title="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          H2
        </ToolbarButton>
        <ToolbarButton title="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          H3
        </ToolbarButton>
        <ToolbarButton title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          • List
        </ToolbarButton>
        <ToolbarButton title="Ordered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          1. List
        </ToolbarButton>
        <ToolbarButton title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          ❝
        </ToolbarButton>
        <ToolbarButton title="Code block" active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
          {"</>"}
        </ToolbarButton>
        <ToolbarButton title="Link" active={editor.isActive("link")} onClick={() => setLink(editor)}>
          🔗
        </ToolbarButton>
        <ToolbarButton title="Image" onClick={() => fileInput.current?.click()}>
          🖼
        </ToolbarButton>
        <ToolbarButton title="Undo" onClick={() => editor.chain().focus().undo().run()}>
          ↶
        </ToolbarButton>
        <ToolbarButton title="Redo" onClick={() => editor.chain().focus().redo().run()}>
          ↷
        </ToolbarButton>
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void pickImage(f);
          e.target.value = "";
        }}
      />

      <div className="px-4 py-3">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
